import {
  isFutureRecordDate,
  isTodayRecordDate,
  parseFlexibleDate,
  toIsoDate,
} from './darwinRecordUtils';

const REQUIRED_FIELD_MESSAGE = 'Please fill all required fields';

export const isValidRecordDate = (value) => Boolean(parseFlexibleDate(value));

export const getRegularizationPayrollWindow = (today = new Date()) => {
  const dayOfMonth = today.getDate();
  const year = today.getFullYear();
  const month = today.getMonth();

  let windowStart;
  let windowEnd;

  if (dayOfMonth > 25) {
    windowStart = new Date(year, month, 26);
    windowEnd = new Date(year, month + 1, 25);
  } else {
    windowStart = new Date(year, month - 1, 26);
    windowEnd = new Date(year, month, 25);
  }

  windowStart.setHours(0, 0, 0, 0);
  windowEnd.setHours(23, 59, 59, 999);

  return { windowStart, windowEnd };
};

const compareTimes = (checkIn, checkOut) => {
  const [inH, inM] = String(checkIn).split(':').map(Number);
  const [outH, outM] = String(checkOut).split(':').map(Number);
  if ([inH, inM, outH, outM].some((n) => Number.isNaN(n))) return 0;
  return outH * 60 + outM - (inH * 60 + inM);
};

/** Client-side checks aligned with fusion-service regularization validation. */
export const validateMarkRegularizationForm = (formData) => {
  const {
    reason_for_missed_attendance,
    location_of_work,
    date,
    check_in_time,
    check_out_time,
    remarks,
  } = formData || {};

  if (
    !reason_for_missed_attendance ||
    !location_of_work ||
    !date ||
    !check_in_time ||
    !check_out_time ||
    !String(remarks || '').trim()
  ) {
    return { valid: false, error: REQUIRED_FIELD_MESSAGE };
  }

  if (!isValidRecordDate(date)) {
    return { valid: false, error: 'Invalid regularization date' };
  }

  if (compareTimes(check_in_time, check_out_time) <= 0) {
    return {
      valid: false,
      error: 'Checkout time cannot be earlier than check-in time.',
    };
  }

  const regularizationDay = parseFlexibleDate(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  regularizationDay.setHours(0, 0, 0, 0);

  if (isFutureRecordDate(date, today)) {
    return {
      valid: false,
      error: 'You cannot apply regularization for a future date.',
    };
  }

  if (isTodayRecordDate(date, today)) {
    return {
      valid: false,
      error: 'You cannot apply regularization for today.',
    };
  }

  const daysDiff = Math.floor((today - regularizationDay) / (1000 * 60 * 60 * 24));
  if (daysDiff > 7) {
    return {
      valid: false,
      error: 'You cannot apply for regularization if the date is more than 7 days old.',
    };
  }

  const { windowStart, windowEnd } = getRegularizationPayrollWindow(today);
  if (regularizationDay < windowStart || regularizationDay > windowEnd) {
    return {
      valid: false,
      error: 'You cannot apply regularization for the previous or future month.',
    };
  }

  return {
    valid: true,
    payload: {
      regularization_date: toIsoDate(date),
      check_in_time,
      check_out_time,
      reason_for_missed_attendance,
      location_of_work,
      message: String(remarks).trim(),
    },
  };
};

export const validateRejectRegularization = (managerRemark) => {
  if (!String(managerRemark || '').trim()) {
    return {
      valid: false,
      error: 'Please enter a remark before rejecting',
    };
  }
  return { valid: true };
};

export const validateRegularizationListFilters = (filters = {}) => {
  const { start_date, end_date } = filters;
  if (start_date && end_date) {
    const start = parseFlexibleDate(start_date);
    const end = parseFlexibleDate(end_date);
    if (start && end && start > end) {
      return {
        valid: false,
        error: 'From date cannot be later than To date',
      };
    }
  }
  return { valid: true };
};
